import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Mails, Plus, RefreshCw } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import Loader from '../components/Loader';
import { Badge, Empty, Toast } from '../components/userProject/UpUi';
import { projectApi } from '../components/userProject/api';

export const CAMPAIGN_STATUS = {
    draft: { label: 'Brouillon', tone: 'muted' },
    ready: { label: 'Prête à envoyer', tone: 'info' },
    sending: { label: 'Envoi en cours', tone: 'live' },
    paused: { label: 'En pause', tone: 'warn' },
    done: { label: 'Terminée', tone: 'ok' },
}

/**
 * Emails (PrimeStation) : les campagnes, envoyées par PrimeStation via Resend. Une campagne = un email (éditeur en
 * blocs, page /emails/:id) + des destinataires (invitations valides, membres…) + l'envoi par lots, piloté d'ici.
 */
const Emails = () => {
    const { setCurrentPage } = useContext(AppContext)
    const navigate = useNavigate()
    const pApi = useMemo(() => projectApi('primestation'), [])
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [toast, setToast] = useState(null)

    useEffect(() => { setCurrentPage('emails') }, [setCurrentPage])
    const load = useCallback(async () => {
        try { setData(await pApi.get('/emails')); setError('') } catch (e) { setError(e.message) }
    }, [pApi])
    useEffect(() => { load() }, [load])

    const create = async () => {
        try {
            const c = await pApi.post('/emails', { name: `Campagne du ${moment().format('DD/MM')}` })
            navigate(`/emails/${c.id}`)
        } catch (e) { setToast({ text: e.message, error: true, at: Date.now() }) }
    }

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page em-page'>
                    <header className='up-head'>
                        <div>
                            <h1>Emails</h1>
                            <p className='up-muted'>Campagnes envoyées par PrimeStation (Resend) : invitations, nouvelles aux membres… Chaque email contient un lien de désinscription.</p>
                        </div>
                        <div className='em-head-actions'>
                            <button className='pay-act-btn' onClick={load} title='Recharger'><RefreshCw size={16} color='#fff' /></button>
                            <button className='btn cs' onClick={create}><Plus size={15} /> Nouvelle campagne</button>
                        </div>
                    </header>
                    {data?.simulation && (
                        <p className='em-sim'>Mode simulation : ce PrimeStation n’a pas de clé Resend, aucun email ne peut partir (tout se déroule, rien n’est envoyé).</p>
                    )}
                    {data && !data.simulation && data.mode?.blocked && (
                        <p className='em-sim'>Envoi réel bloqué ici : les liens des emails mèneraient à {data.mode.links}. Les invitations partent du PrimeStation en ligne.</p>
                    )}
                    {error && <p className='up-error'>{error}</p>}
                    {!data && !error && <div className='up-loading'><Loader /></div>}
                    {data && !data.items.length && <Empty>Aucune campagne. Crée la première : l’email d’invitation est déjà prêt à personnaliser.</Empty>}
                    <div className='em-list'>
                        {data?.items.map((c) => {
                            const pct = c.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0
                            return (
                                <button key={c.id} className='em-card' onClick={() => navigate(`/emails/${c.id}`)}>
                                    <span className='em-card-top'>
                                        <Mails size={18} />
                                        <b>{c.name}</b>
                                        <Badge tone={CAMPAIGN_STATUS[c.status]?.tone}>{CAMPAIGN_STATUS[c.status]?.label || c.status}</Badge>
                                        {c.simulated && <Badge>Simulation</Badge>}
                                    </span>
                                    <span className='em-card-subject'>{c.subject}</span>
                                    <span className='up-muted'>{c.audienceLabel}{c.withCode ? ' · code d’invitation unique' : ''}</span>
                                    {c.total > 0 && (
                                        <span className='em-progress'>
                                            <i style={{ width: `${pct}%` }} />
                                            <small>{c.sent.toLocaleString('fr-FR')} envoyés / {c.total.toLocaleString('fr-FR')}{c.failed ? ` · ${c.failed} en échec` : ''}</small>
                                        </span>
                                    )}
                                    <span className='up-muted em-card-date'>Créée {moment(c.createdAt).fromNow()}{c.createdBy ? ` par ${c.createdBy}` : ''}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
                <Toast toast={toast} onDone={() => setToast(null)} />
            </CenterLayout>
        </MainLayout>
    );
};

export default Emails;
